import Resumable from '../../../vendor/resumable/resumable';
import { v4 as uuidv4 } from 'uuid';
import forOwn from 'lodash/forOwn';

import { makeRequest } from '../../../lib/api';
import { store } from '../../../lib/redux';
import APPConf from '../../../lib/config/common';
import { NO_REGION } from '../constants/fias';

const ORDER_PATH = '/v1/orders';
const CARMEN_PATH = '/v1/carmen';
const ORDER_FIELD_PATH = '/v1/order_fields';
const ORDER_RESPONSIBLE_USERS_PATH = '/v1/order_responsibles';
const ORDER_DAMAGED_ITEMS_PATH = '/v1/damaged_items/orders';
const ORDER_BLOCK = '/v1/order_block';

class ordersController {
  getOrderFieldsForService = (serviceId) => {
    return makeRequest('get', `${ ORDER_FIELD_PATH }/services/${ serviceId }`);
  };

  getOrdersList = (data = {}) => {
    let statusRefValues;
    if (!data.statusRefValues || Array.isArray(data.statusRefValues)) {
      statusRefValues = data.statusRefValues;
    } else {
      statusRefValues = [data.statusRefValues];
    }
    const regionIds = data.regionIds ? data.regionIds.map(v => (v.value === NO_REGION ? null : v.value)) : null;
    const createdByUserIds = data.createdByUserIds ? data.createdByUserIds.map(v => v.value) : null;
    const executorUserIds = data.executorUserIds ? data.executorUserIds.map(v => v.value) : null;
    const orderResponsibleUserIds = data.orderResponsibleUserIds
      ? data.orderResponsibleUserIds.map(v => v.value)
      : null;
    return makeRequest('post', `${ ORDER_PATH }/find${ data?.showIsPaid ? '?showIsPaid=true' : '' }`, {
      ...data,
      statusRefValues,
      regionIds,
      createdByUserIds,
      executorUserIds,
      orderResponsibleUserIds,
      sortField: (data?.sortField ?? 'none') === 'createdDate' ? 'createdAt' : data?.sortField,
    });
  };

  getOrder = (id, options = {}) => {
    return makeRequest('get', `${ ORDER_PATH }/${ id }`, {}, {
      cancelToken: options.cancelToken, // Пробрасываем cancelToken
    });
  };

  createOrder = (data = {}) => {
    return makeRequest('post', `${ ORDER_PATH }`, data);
  };

  updateOrder = (data = {}) => {
    const request = {
      ...data,
      carmenLeadId: (data?.carmenLeadId === '' ? null : data?.carmenLeadId),
      maId: (data?.maId === '' ? null : data?.maId),
      userId: (((data?.userId === '') || (!data?.userId)) ? null : data?.userId),
      cost: (data?.cost && (typeof data?.cost === 'string')) ? data?.cost.replace(',', '.') : data?.cost,
    };
    return makeRequest('put', `${ ORDER_PATH }/${ data.id }`, request);
  };

  deleteOrder = (orderId) => {
    return makeRequest('delete', `${ ORDER_PATH }/${ orderId }`);
  };

  getOrderPhotos = (orderId, showDeleted, sourceRefValues) => {
    let showDeletedString = showDeleted ? '?show-deleted=true' : '?show-deleted=false';
    if (sourceRefValues && (sourceRefValues.length > 0)) {
      sourceRefValues.forEach(v => {
        showDeletedString = `${ showDeletedString }&source=${ String(v) }`;
      });
    }
    return makeRequest('get', `${ ORDER_PATH }/${ orderId }/photos${ showDeletedString }`);
  };

  getOrderClientPhotos = (orderId, showDeleted) => {
    const showDeletedString = showDeleted ? '?show-deleted=true' : '';
    return makeRequest('get', `${ ORDER_PATH }/${ orderId }/client/photos${ showDeletedString }`);
  };

  uploadOrderChunkedMedia = (endpoint, data, chunkSizeMb = 0.5) => {
    const { file, ...rest } = data;
    return new Promise((resolve, reject) => {
      const r = new Resumable({
        target: `${ APPConf.serverAddress }${ endpoint }`,
        headers: { 'Authorization': `${ store.getState().auth.profile.token }` },
        testChunks: false,
        chunkSize: chunkSizeMb * 1024 * 1024,
        query: { ...rest },
        generateUniqueIdentifier: f => {
          return `${ f.size }-${ uuidv4() }`;
        },
      });
      r.addFile(file);
      r.on('fileAdded', () => {
        r.upload();
      });
      r.on('fileSuccess', resumableFile => {
        resolve(resumableFile);
      });
      r.on('fileError', (resumableFile, message) => {
        reject(message);
      });
    });
  };

  createOrderPhoto = (data) => {
    const { orderId, ...rest } = data;
    const form = new FormData();
    forOwn(rest, (value, key) => {
      form.append(key, value);
    });
    return makeRequest('post', `${ ORDER_PATH }/${ orderId }/photos`, form);
  };

  updateOrderPhoto = (data) => {
    return makeRequest('put', `${ ORDER_PATH }/photos/${ data.photoId }`, data);
  };

  restoreOrderPhoto = (photoId) => {
    return makeRequest('put', `${ ORDER_PATH }/photos/${ photoId }/restore`);
  };

  deleteOrderPhoto = (photoId) => {
    return makeRequest('delete', `${ ORDER_PATH }/photos/${ photoId }`);
  };

  checkOrderFile = (fileId) => {
    return makeRequest('get', `${ ORDER_PATH }/file/${ fileId }/check`);
  };

  createOrderVideo = (data) => {
    return this.uploadOrderChunkedMedia(`/v1/orders/${ data.orderId }/resumable`, data);
  };

  getOrderComments = (orderId) => {
    return makeRequest('get', `${ ORDER_PATH }/${ orderId }/comments`);
  };

  deleteOrderComment = (orderId) => {
    return makeRequest('delete', `${ ORDER_PATH }/comments/${ orderId }`);
  };

  createOrderComment = (data) => {
    return makeRequest('post', `${ ORDER_PATH }/comments/`, data);
  };

  getOrderTemporaryFiles = (data) => {
    // if (data.sourceRefValueId) return makeRequest('get', `${ ORDER_PATH }/${ data.orderId }/temporaryFiles/?sourceRefValueId=${ data.sourceRefValueId }`);
    return makeRequest('get', `${ ORDER_PATH }/${ data.orderId }/temporaryFiles/`);
  };

  createOrderTemporaryFiles = (data) => {
    return makeRequest('post', `${ ORDER_PATH }/${ data.orderId }/createZip`, data);
  };

  getCarmenStatuses = (orderId) => {
    return makeRequest('get', `${ CARMEN_PATH }/order/${ orderId }`);
  };

  getOrderResponsibleUsers = data => {
    return makeRequest('get', `${ ORDER_RESPONSIBLE_USERS_PATH }/${ data.orderId }`);
  };

  addOrderResponsibleUser = data => {
    return makeRequest('post', `${ ORDER_RESPONSIBLE_USERS_PATH }`, data);
  };

  deleteOrderResponsibleUser = data => {
    return makeRequest('delete', `${ ORDER_RESPONSIBLE_USERS_PATH }/${ data.id }`);
  };

  getTimeZone = data => {
    return makeRequest('get', `/v1/geo/timezone/${ data.fiasId }`);
  };

  getOrderExecutors = data => {
    return makeRequest('get', `${ ORDER_PATH }/revision/executors/${ data.orderId }`);
  };

  getOrderEditors = data => {
    return makeRequest('get', `${ ORDER_PATH }/revision/editors/${ data.orderId }`);
  };

  // Блок управление повреждения автомобиля
  getDamagedItems = orderId => {
    return makeRequest('get', `${ ORDER_DAMAGED_ITEMS_PATH }/${ orderId }/presence`);
  };

  deleteDamagedItems = (data) => {
    return makeRequest('delete', `${ ORDER_DAMAGED_ITEMS_PATH }?damagedItemId=${ data.damagedItemId }&orderId=${ data.orderId }`);
  };

  createDamagedItems = (data) => {
    return makeRequest('post', `${ ORDER_DAMAGED_ITEMS_PATH }`, data);
  };
  // Конец блока управление повреждения автомобиля

  blockOrder = (data) => {
    return makeRequest('put', `${ ORDER_BLOCK }/${ data.orderId }`);
  };

  unblockOrder = (data) => {
    return makeRequest('delete', `${ ORDER_BLOCK }/${ data.orderId }`);
  };

  getOrderHistory = (data) => {
    return makeRequest('get', `${ ORDER_PATH }/${ data.orderId }/history`);
  };

  // специальный метод - меняет тип заявки
  changeOrderService = (data) => {
    return makeRequest('post', `${ ORDER_FIELD_PATH }/move_service/${ data.orderId }`, data);
  };
}

export default new ordersController();
